import { Button, TextField, Box, IconButton } from "@mui/material";
import AppTable from "components/Shared/AppTable";
import TableFilter from "components/Shared/AppTable/TableFilter";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useAppMeta, useTablePagination } from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import { useLazyGetResponsibleStatsExportQuery, useLazyGetResponsibleStatsQuery } from "store/services/reportApi";
import { exportExcelFile } from "utils/exportExcelFile";
import { InfoOutlined } from "@mui/icons-material";

const breadcrumbs = [
    { title: "Reports", path: "/app/crm/reports/list" },
    { title: "Vendor Lead Responsible Stats" },
];

export const VendorLeadResponsibleStatsScreen = () => {
    const navigate = useNavigate();
    const { params } = useCustomParams();
    const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
        useTablePagination();

    const keyword = params?.search || "";

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [isExporting, setIsExporting] = useState(false);

    // Paginated list query
    const [fetchStats, { data: statsRes, isLoading, isFetching }] =
        useLazyGetResponsibleStatsQuery();

    // Separate export query — isolated cache, never touches statsRes
    const [fetchStatsExport] = useLazyGetResponsibleStatsExportQuery();

    const baseParams = {
        keyword,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
    };

    React.useEffect(() => {
        fetchStats({
            ...baseParams,
            page: currentPage,
            limit: perPage,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [keyword, currentPage, perPage, startDate, endDate, fetchStats]);

    useAppMeta("Vendor Lead Responsible Stats");

    const handleExport = async () => {
        try {
            setIsExporting(true);
            const result = await fetchStatsExport(baseParams).unwrap();

            const fileNameParts = ["Responsible Stats"];
            if (startDate) fileNameParts.push(`from ${startDate}`);
            if (endDate) fileNameParts.push(`to ${endDate}`);

            exportExcelFile({
                sheetData: result.data,
                fileName: fileNameParts.join(" "),
            });
        } catch (err) {
            console.error("Export failed:", err);
        } finally {
            setIsExporting(false);
        }
    };

    const handleRowClick = (row) => {
        navigate(
            `/app/crm/reports/vendor-responsible/${row._id}/breakdown`,
            { state: { userName: row.user?.name } }
        );
    };

    const columns = [
        {
            name: "User Name",
            selector: (row) => row?.user?.name,
            cell: (row) => <div>{row?.user?.name ?? "N/A"}</div>,
            minWidth: "180px",
        },
        {
            name: "Total Assigned",
            selector: (row) => row?.totalAssigned,
            cell: (row) => (
                <div className="font-medium">{row?.totalAssigned ?? 0}</div>
            ),
            minWidth: "130px",
        },
        {
            name: "New",
            selector: (row) => row?.new,
            cell: (row) => <div>{row?.new ?? 0}</div>,
            minWidth: "80px",
        },
        {
            name: "Contacted",
            selector: (row) => row?.contacted,
            cell: (row) => <div>{row?.contacted ?? 0}</div>,
            minWidth: "110px",
        },
        {
            name: "Quote Sent",
            selector: (row) => row?.quoteSent,
            cell: (row) => <div>{row?.quoteSent ?? 0}</div>,
            minWidth: "110px",
        },
        {
            name: "Negotiation",
            selector: (row) => row?.negotiation,
            cell: (row) => <div>{row?.negotiation ?? 0}</div>,
            minWidth: "120px",
        },
        {
            name: "Won",
            selector: (row) => row?.won,
            cell: (row) => <div>{row?.won ?? 0}</div>,
            minWidth: "80px",
        },
        {
            name: "Lost",
            selector: (row) => row?.lost,
            cell: (row) => <div>{row?.lost ?? 0}</div>,
            minWidth: "80px",
        },
        {
            name: "Actions",
            cell: (row) => (
                <IconButton
                    size="small"
                    aria-label="View Breakdown"
                    color="primary"
                    onClick={() =>
                        navigate(
                            `/app/crm/reports/vendor-responsible/${row._id}/breakdown`,
                            { state: { userName: row.user?.name } }
                        )
                    }
                >
                    <InfoOutlined />
                </IconButton>
            ),
            ignoreRowClick: true,
            right: true,
            minWidth: "80px",
        },
    ];

    return (
        <BreadcrumbLayout
            breadcrumbs={breadcrumbs}
            isBusy={isFetching || isLoading}
        >
            <BreadcrumbLayout.Paper>
                <Box
                    sx={{
                        px: 2,
                        pt: 2,
                        pb: 1,
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                    }}
                >
                    <TextField
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />

                    <TableFilter searchLabel="Search by User Name" />

                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setStartDate(""); setEndDate(""); }}
                        disabled={!startDate && !endDate}
                    >
                        Clear
                    </Button>

                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleExport}
                        disabled={isExporting || isLoading || isFetching}
                    >
                        {isExporting ? "Exporting..." : "Export Excel"}
                    </Button>
                </Box>

                <AppTable
                    columns={columns}
                    data={statsRes?.data}
                    paginationServer
                    paginationPerPage={perPage}
                    paginationDefaultPage={currentPage}
                    onChangePage={handlePageChange}
                    onChangeRowsPerPage={handlePerRowsChange}
                    progressPending={isLoading || isFetching}
                    paginationTotalRows={statsRes?.totalSize ?? 0}
                />
            </BreadcrumbLayout.Paper>
        </BreadcrumbLayout>
    );
};